// =============================================================================
// ui/tabs/tradeColumns.ts — the trades table's columns, and how it sorts.
// -----------------------------------------------------------------------------
// Transcribed from the old client's `TRADE_COLUMNS_DATAVIEW` + `compareTrades`.
// Sixteen columns in this exact order; the ported tab showed seven, and the eight
// that were missing were not even in the loaded projection (D57).
//
// ⚠ THIS IS PRESENTATION, NOT DERIVATION. A comparator subtracts, which is the
// only arithmetic in `ui/` — it produces an ORDER, never a number on screen.
// Every value here comes from a column the database already holds or from
// `effectiveTradeDate`, which is the day-boundary rule, not a calculation.
//
// Kept in its own module so the ordering rules are testable without a DOM, and
// so the column list reads as a list rather than as markup.
// =============================================================================

import { effectiveTradeDate, type DayBoundaryMode } from '../../selectors/population';
import { tradeDisciplinePct, type GaugeTrade } from '../../selectors/dashGauges';
import type { TradeTableRow } from '../../io/sync/trades';

export type SortDir = 'asc' | 'desc';

export interface TradeSort {
  key: string;
  dir: SortDir;
}

/**
 * The default: most-recent-first, `timeIn` breaking ties.
 *
 * ⚠ The tiebreaker is load-bearing and was MISSING in the first port. Sorting on
 * the date alone leaves intra-day order to whatever Postgres returned — the
 * ported column read 10:01, 01:08, 01:00, 09:59 against a cleanly descending
 * one in the live app. The test that was supposed to cover ordering used three
 * trades on three different dates, so it could not see it.
 */
export const DEFAULT_SORT: TradeSort = { key: 'date', dir: 'desc' };

export interface TradeColumn {
  key: string;
  label: string;
  sortable: boolean;
  /** Right-aligned + `num` class, and compared numerically. */
  numeric: boolean;
  accessor: (t: TradeTableRow, mode: DayBoundaryMode) => string | number | null;
}

export const TRADE_TABLE_COLUMNS: readonly TradeColumn[] = [
  {
    key: 'date', label: 'Date', sortable: true, numeric: false,
    // ⚠ effectiveTradeDate, never `t.date` — in session mode a 22:36 trade
    // belongs to the NEXT session, and reading the raw field puts a different
    // day's trades under the same heading. The `timeIn` suffix is what makes
    // this the primary sort AND the tiebreaker in one key, exactly as the old
    // client does it.
    accessor: (t, mode) => `${effectiveTradeDate(t, mode) || ''} ${t.timeIn ?? ''}`,
  },
  { key: 'time', label: 'Time In', sortable: true, numeric: false, accessor: (t) => t.timeIn ?? '' },
  { key: 'timeout', label: 'Time Out', sortable: true, numeric: false, accessor: (t) => t.timeOut ?? '' },
  { key: 'symbol', label: 'Sym', sortable: true, numeric: false, accessor: (t) => t.contract ?? '' },
  // The account LABEL is resolved against loaded accounts, which this module
  // does not have — the tab passes a resolver into `sortTrades` instead.
  { key: 'account', label: 'Account', sortable: true, numeric: false, accessor: () => '' },
  { key: 'side', label: 'Side', sortable: true, numeric: false, accessor: (t) => t.side ?? '' },
  { key: 'qty', label: 'Qty', sortable: true, numeric: true, accessor: (t) => t.qty ?? null },
  { key: 'entry', label: 'Entry', sortable: true, numeric: true, accessor: (t) => t.entry ?? null },
  { key: 'exit', label: 'Exit', sortable: true, numeric: true, accessor: (t) => t.exit ?? null },
  { key: 'r', label: 'R', sortable: true, numeric: true, accessor: (t) => t.rrRealized ?? null },
  { key: 'mae', label: 'MAE', sortable: true, numeric: true, accessor: (t) => t.maePoints ?? null },
  { key: 'points', label: 'PTS', sortable: true, numeric: true, accessor: (t) => t.points ?? null },
  { key: 'mfe', label: 'MFE', sortable: true, numeric: true, accessor: (t) => t.mfePoints ?? null },
  {
    key: 'grossPnl', label: 'Gross P&L', sortable: true, numeric: true,
    accessor: (t) => grossPnlOf(t),
  },
  { key: 'fees', label: 'Commissions', sortable: true, numeric: true, accessor: (t) => t.fees ?? 0 },
  { key: 'pnl', label: 'Net P&L', sortable: true, numeric: true, accessor: (t) => t.netPnl ?? null },
];

/**
 * ── 🚨 THE OTHER COLUMN SET — THE ONE EVERY TAB EXCEPT TRADES USES (#45) ──
 *
 * The old client has TWO layouts and this port only had one. `TRADE_TABLE_COLUMNS`
 * above is beta's `TRADE_COLUMNS_DATAVIEW`, which is the **Trades-tab-only**
 * accounting view: it drops the four review columns and adds Gross P&L and
 * Commissions.
 *
 * The Dashboard's Recent Trades strip asked for that set, so a glance at your
 * last six trades **lost the four fields it exists for** — which setup it was,
 * its grade, its discipline score and its tags — and gained two accounting
 * columns beta deliberately keeps off that screen.
 *
 * ⚠ NO NEW READ. `patternId`, `grade`, `booleans` and `mistakeTags` are already
 * on `TABLE_KEYS`, each with a comment explaining what else needed it. The data
 * was there the whole time; only the layout was missing.
 *
 * ⚠ ORDER DIFFERS BEYOND THE ADDED COLUMNS. Here it runs
 * `… exit · Pts · Net P&L · R · MAE · MFE …`; the accounting view runs
 * `… exit · R · MAE · PTS · MFE · Gross · Commissions · Net`. That is beta's
 * arrangement in both cases and the reason it kept two lists rather than
 * reordering one — see the PTS-between-MAE-and-MFE test, which is about the
 * accounting view specifically.
 *
 * ⚠ NO `actions` COLUMN, unlike beta. This port has no per-row action menu on
 * either table yet, and a header over an empty cell is worse than neither.
 */
export const TRADE_TABLE_COLUMNS_DEFAULT: readonly TradeColumn[] = [
  {
    key: 'date', label: 'Date', sortable: true, numeric: false,
    accessor: (t, mode) => `${effectiveTradeDate(t, mode) || ''} ${t.timeIn ?? ''}`,
  },
  { key: 'time', label: 'Time In', sortable: true, numeric: false, accessor: (t) => t.timeIn ?? '' },
  { key: 'timeout', label: 'Time Out', sortable: true, numeric: false, accessor: (t) => t.timeOut ?? '' },
  { key: 'symbol', label: 'Sym', sortable: true, numeric: false, accessor: (t) => t.contract ?? '' },
  { key: 'account', label: 'Account', sortable: true, numeric: false, accessor: () => '' },
  { key: 'side', label: 'Side', sortable: true, numeric: false, accessor: (t) => t.side ?? '' },
  { key: 'qty', label: 'Qty', sortable: true, numeric: true, accessor: (t) => t.qty ?? null },
  { key: 'entry', label: 'Entry', sortable: true, numeric: true, accessor: (t) => t.entry ?? null },
  { key: 'exit', label: 'Exit', sortable: true, numeric: true, accessor: (t) => t.exit ?? null },
  // ⚠ `Pts`, not `PTS`. Beta's two lists genuinely spell it differently, and a
  // parity diff of the header row is one of the checks this fix exists to pass.
  { key: 'points', label: 'Pts', sortable: true, numeric: true, accessor: (t) => t.points ?? null },
  { key: 'pnl', label: 'Net P&L', sortable: true, numeric: true, accessor: (t) => t.netPnl ?? null },
  { key: 'r', label: 'R', sortable: true, numeric: true, accessor: (t) => t.rrRealized ?? null },
  { key: 'mae', label: 'MAE', sortable: true, numeric: true, accessor: (t) => t.maePoints ?? null },
  { key: 'mfe', label: 'MFE', sortable: true, numeric: true, accessor: (t) => t.mfePoints ?? null },
  // Resolved against loaded patterns, which this module does not have — the tab
  // passes a resolver, exactly as it does for Account.
  { key: 'pattern', label: 'Pattern', sortable: true, numeric: false, accessor: () => '' },
  { key: 'grade', label: 'Grade', sortable: true, numeric: false, accessor: (t) => t.grade ?? '' },
  {
    key: 'disc', label: 'Disc.', sortable: true, numeric: true,
    // ⚠ NULL for an unreviewed trade, never 0 and never 100. The breach flags
    // are stored as "absent means it did not happen", so an untouched import has
    // six absent flags and would otherwise render a perfect score with nothing
    // judged. Shared with the Discipline gauge so the two cannot disagree about
    // the same trade on the same screen.
    accessor: (t) => tradeDisciplinePct(t as unknown as GaugeTrade),
  },
  { key: 'tags', label: 'Tags', sortable: false, numeric: false, accessor: (t) => (t.mistakeTags ?? []).join(', ') },
];

/**
 * Grade sorts A+ → F, not alphabetically.
 *
 * ⚠ Alphabetically `A+` sorts BEFORE `A` only by accident of punctuation, and
 * `F` would land between `D` and nothing. Ported from beta's `GRADE_ORDER`;
 * anything unrecognised ranks last so ungraded trades do not lead the list.
 */
const GRADE_ORDER: Record<string, number> = { 'A+': 0, A: 1, B: 2, C: 3, D: 4, F: 5 };

/**
 * Gross P&L, falling back to `net + fees` when the column is null.
 *
 * ⚠ The fallback is the old client's and it matters: `gross_pnl` is null for
 * every trade imported before the column existed, and a null there would show a
 * blank Gross column beside a populated Net one on exactly the historical trades
 * a user is most likely to be reviewing.
 */
export function grossPnlOf(t: TradeTableRow): number | null {
  if (t.grossPnl != null) return t.grossPnl;
  if (t.netPnl == null && t.fees == null) return null;
  return (t.netPnl ?? 0) + (t.fees ?? 0);
}

export type AccountLabeller = (t: TradeTableRow) => string;

/** Same idea as `AccountLabeller`: a pattern's NAME lives in a registry this
    module does not have, so the tab resolves it. */
export type PatternLabeller = (t: TradeTableRow) => string;

/**
 * Compare two rows on one column.
 *
 * ⚠ NULLS ALWAYS SORT LAST, in both directions — ported literally. It is not
 * "null is smallest": ascending by MAE should surface the tightest trades, and
 * if nulls led, the top of that list would be the trades with no MAE at all,
 * which is the opposite of what the user asked to see.
 */
export function compareTrades(
  a: TradeTableRow,
  b: TradeTableRow,
  sort: TradeSort,
  mode: DayBoundaryMode,
  labelAccount: AccountLabeller,
  /**
   * ⚠ WHICH LAYOUT IS BEING SORTED. Beta passes a `dataView` flag for the same
   * reason: the two sets do not share every key, so a sort on `pattern` or
   * `disc` finds nothing in the accounting list and silently returns 0 — a
   * header you can click that does nothing.
   *
   * Defaults to the accounting set, so every existing caller is unchanged.
   */
  columns: readonly TradeColumn[] = TRADE_TABLE_COLUMNS,
  labelPattern?: PatternLabeller,
): number {
  const col = columns.find((c) => c.key === sort.key);
  if (!col || !col.sortable) return 0;

  const get = (t: TradeTableRow) => {
    if (col.key === 'account') return labelAccount(t);
    if (col.key === 'pattern') return labelPattern ? labelPattern(t) : '';
    return col.accessor(t, mode);
  };

  const av = get(a);
  const bv = get(b);

  // ⚠ A+ → F, not alphabetical. See GRADE_ORDER.
  if (col.key === 'grade') {
    const ai = GRADE_ORDER[String(av)] ?? 99;
    const bi = GRADE_ORDER[String(bv)] ?? 99;
    return sort.dir === 'asc' ? ai - bi : bi - ai;
  }

  if (col.numeric) {
    const an = av == null || Number.isNaN(Number(av));
    const bn = bv == null || Number.isNaN(Number(bv));
    if (an && bn) return 0;
    if (an) return 1;
    if (bn) return -1;
    return sort.dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av);
  }

  const as = String(av).toLowerCase();
  const bs = String(bv).toLowerCase();
  if (as < bs) return sort.dir === 'asc' ? -1 : 1;
  if (as > bs) return sort.dir === 'asc' ? 1 : -1;
  return 0;
}

export function sortTrades(
  rows: readonly TradeTableRow[],
  sort: TradeSort,
  mode: DayBoundaryMode,
  labelAccount: AccountLabeller,
  columns: readonly TradeColumn[] = TRADE_TABLE_COLUMNS,
  labelPattern?: PatternLabeller,
): TradeTableRow[] {
  return [...rows].sort(
    (a, b) => compareTrades(a, b, sort, mode, labelAccount, columns, labelPattern),
  );
}

/** Clicking the active column flips it; clicking another starts it descending. */
export function nextSort(current: TradeSort, key: string): TradeSort {
  if (current.key !== key) return { key, dir: 'desc' };
  return { key, dir: current.dir === 'desc' ? 'asc' : 'desc' };
}
